import { branchService } from '@/_service/branch.service'
import { getCheckPointDeliveryZones } from '@/_service/zone.service'
import { IRedux } from '@/_store/types'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearOrderIds,
  clearBranchData,
  setDeliveryZone,
} from '@/_store/common/common.slice'
import { useSaveBranchData } from '@/_hooks/useSaveBranchData'
import { useActiveBrand } from '@/_hooks/useActiveBrand'
import dayjs from 'dayjs'

export default function useDelivery({ enabled = true }: { enabled?: boolean }) {
  const shipperId = useActiveBrand()
  const { order_type } = useSelector((state: IRedux) => state.session)
  const commonState = useSelector((state: IRedux) => state.common)
  const { future_time } = useSelector((state: IRedux) => state.checkout)

  const dispatch = useDispatch()
  const saveBranch = useSaveBranchData()

  const activePoint = useMemo(() => {
    // Only calculate if we're in delivery mode and have points
    if (order_type?.type === 'delivery' && commonState?.points?.length > 0) {
      // Find the first active point
      const active = commonState.points.find((point) => point.isActive)

      if (active?.location) {
        return {
          lat: active.location[0] ?? 0,
          long: active.location[1] ?? 0,
        }
      }
    }

    return null
  }, [commonState?.points, order_type?.type])

  useEffect(() => {
    const fetchDeliveryZoneAndNearestBranch = async () => {
      const isPreorder = future_time && dayjs(future_time).isAfter(dayjs())

      if (!activePoint?.lat || !activePoint?.long || isPreorder || !shipperId) {
        return
      }

      try {
        // 1. Fetch and set delivery zone for this brand (same as map logic)
        const zoneRes = await getCheckPointDeliveryZones(
          { lat: activePoint.lat, lon: activePoint.long },
          shipperId
        )
        const currentZone = zoneRes?.data?.result?.[0]
        if (currentZone?.id && commonState.deliveryZones?.[shipperId]?.id !== currentZone.id) {
          dispatch(setDeliveryZone({ shipper_id: shipperId, zone: { id: currentZone.id, min_order_amount: currentZone.min_order_amount } }))
        } else {
          dispatch(setDeliveryZone({ shipper_id: shipperId, zone: null }))
        }

        // 2. Find nearest branch using the zone we just fetched (don't rely on state update)
        const branchResult = await branchService.findNearest(
          {
            lat: activePoint.lat,
            long: activePoint.long,
            order_type_id: order_type?.id,
            delivery_zone_id: currentZone?.id || '',
          },
          shipperId
        )
        const nearestBranch = branchResult?.data?.branches?.find((b) => b?.is_active) ?? branchResult?.data?.branches?.[0]
        if (nearestBranch?.id) {
          saveBranch({
            id: nearestBranch.id,
            menu_id: nearestBranch.menu_id || '',
            address: nearestBranch.address,
            name: nearestBranch.name,
            location: nearestBranch.location,
            slug: nearestBranch.slug,
            future_delivery_order_time: nearestBranch.future_delivery_order_time,
            future_self_pickup_order_time: nearestBranch.future_self_pickup_order_time,
            work_hour_start: nearestBranch.work_hour_start,
            work_hour_end: nearestBranch.work_hour_end,
            is_work_24_7: nearestBranch.is_work_24_7,
            delivery_time: nearestBranch.delivery_time,
            shipper_id: shipperId,
          })
        } else {
          dispatch(clearBranchData(shipperId))
        }
      } catch (error) {
        console.log(error)
      }
    }

    if (commonState?.orderIds) {
      dispatch(clearOrderIds())
    }

    if (enabled) {
      fetchDeliveryZoneAndNearestBranch()
    }
  }, [
    activePoint,
    future_time,
    shipperId,
    order_type?.id,
    enabled,
    saveBranch,
    dispatch,
    commonState?.orderIds,
  ])

  return {}
}
